import { type FC, Fragment, useState } from 'react';
import cn from 'classnames';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import { graphql, useFragment } from 'react-relay';
import { filterNulls } from 'dibs-ts-utils/src/filterNulls';
import { dibsLazyClient } from 'dibs-lazy/src/dibsLazyClient';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency';
import { usePdpCreatorDetails } from '../hooks/usePdpCreatorDetails';
import {
    INTERFACE_STATE,
    useItemTranslations,
} from '../PdpSharedOriginalLanguageProvider/PdpSharedOriginalLanguageProvider';

// components
import {
    ExpandingAreaWithReadMore,
    type OnReadMoreClickArgs,
} from 'dibs-elements/exports/ExpandingArea';
import { Link } from 'dibs-elements/src/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { ClientSuspense } from 'dibs-elements/src/ClientSuspense';
import TranslationToggle from 'dibs-elements/exports/TranslationToggle';
import { PdpRespSidebarDetailsSection } from './PdpRespSidebarDetailsSection';
import { PdpSharedDetailRingSize } from '../shared/PdpSharedDetail/PdpSharedDetailRingSize';
import { PdpSharedDetailItemInvoice } from '../shared/PdpSharedDetail/PdpSharedDetailItemInvoice';
import { PdpSharedStoryTellingShopAllLink } from '../shared/PdpSharedStoryTelling/PdpSharedStoryTellingShopAllLink';

// types
import { type PdpRespSidebarDetailsAbout_item$key as Item } from './__generated__/PdpRespSidebarDetailsAbout_item.graphql';
import { type PdpRespSidebarDetailsAbout_user$key as User } from './__generated__/PdpRespSidebarDetailsAbout_user.graphql';

// styles
import dibsCss from 'dibs-css';

const PdpSharedConditionModal = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedConditionModalWrapper" */ '../PdpSharedModals/PdpSharedConditionModal/PdpSharedConditionModalWrapper'
        )
);

const {
    STEP_INTERACTION_VIEW_MORE_CLICKED,
    STEP_INTERACTION_VIEW_LESS_CLICKED,
    STEP_INTERACTION_VIEW_CONDITION_CLICK,
} = stepInteractionConstants;

const { EVENT_PRODUCT_INTERACTION } = eventNameConstants;
const { INTERACTION_TYPE_PDP } = interactionTypeConstants;

const gaTrigger = 'features';

const trackReadMoreClick = ({ type }: OnReadMoreClickArgs): void =>
    trackEvent({
        eventName: EVENT_PRODUCT_INTERACTION,
        interaction_type: INTERACTION_TYPE_PDP,
        step_interaction_name:
            type === 'readMore'
                ? STEP_INTERACTION_VIEW_MORE_CLICKED
                : STEP_INTERACTION_VIEW_LESS_CLICKED,
        trigger: gaTrigger,
    });

const trackConditionClick = (): void =>
    trackEvent({
        eventName: EVENT_PRODUCT_INTERACTION,
        interaction_type: INTERACTION_TYPE_PDP,
        step_interaction_name: STEP_INTERACTION_VIEW_CONDITION_CLICK,
        trigger: gaTrigger,
    });

const About = (
    <FormattedMessage
        id="PdpRespSidebarDetailsAbout.aboutTheItem"
        defaultMessage="About the Item"
    />
);

const Creator = (
    <FormattedMessage id="PdpRespSidebarDetailsAbout.Creator" defaultMessage="Creator" />
);
const ReadMore = (
    <FormattedMessage
        id="PdpRespSidebarDetailsAbout.readMore"
        defaultMessage="Read More About the Item"
    />
);
const ReadLess = (
    <FormattedMessage
        id="PdpRespSidebarDetailsAbout.readLess"
        defaultMessage="Read Less About the Item"
    />
);
const ViewMore = (
    <FormattedMessage id="PdpRespSidebarDetailsAbout.viewMore" defaultMessage="View More Details" />
);
const ViewLess = (
    <FormattedMessage id="PdpRespSidebarDetailsAbout.viewLess" defaultMessage="View Less Details" />
);

const CUSTOM_DETAILS = ['CONDITION', 'CREATOR', 'ICONIC_DESIGN', 'REFERENCE_NUMBER'];

const itemFragment = graphql`
    fragment PdpRespSidebarDetailsAbout_item on Item {
        isSold
        vertical
        serviceId
        isAvailable
        seoDescription
        hasPrivateListing
        isListingUnlisted
        hasActiveHoldForBuyer(buyerId: $userId) @include(if: $fetchUser)
        seller {
            isAccessible
        }
        condition {
            state
        }
        pdpDetailsDisplay {
            detailKey
            delimiter
            detailDisplayName
            detailValues {
                skuId
                browseUrl
                detailKey
                displayName
                displayDetails
                ...PdpSharedDetailRingSize_detailValues
            }
        }
        storyTellingModule(shortTitles: true) {
            tabs {
                tabType
                tabTitle
                contentHeader
                contentBodyText
                ...PdpSharedStoryTellingShopAllLink_refactor_storyTellingTab
            }
        }
        ...usePdpCreatorDetails_item
        ...PdpSharedDetailItemInvoice_item
    }
`;

const userFragment = graphql`
    fragment PdpRespSidebarDetailsAbout_user on User {
        ...PdpSharedDetailItemInvoice_user
    }
`;

export const PdpRespSidebarDetailsAbout: FC<{
    item: Item;
    user: User | null;
    skuId: string | null;
}> = ({ item: itemRef, user: userRef, skuId }) => {
    const { currency } = useCurrency();
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const [translationState, dispatch] = useItemTranslations();
    const { uniqueCreators, creatorLinkMap } = usePdpCreatorDetails({ item });
    const [isConditionModalOpen, setIsConditionModalOpen] = useState<boolean>(false);
    const {
        isSold,
        seller,
        vertical,
        isAvailable,
        seoDescription,
        hasPrivateListing,
        isListingUnlisted,
        serviceId: itemId,
        pdpDetailsDisplay,
        storyTellingModule,
        hasActiveHoldForBuyer,
    } = item;

    if (!itemId || !pdpDetailsDisplay || !pdpDetailsDisplay.length) {
        return null;
    }

    const interfaceState = translationState?.interfaceState;
    const showOriginal = interfaceState === INTERFACE_STATE.SHOW_ORIGINAL;
    const description = showOriginal
        ? translationState?.data?.viewer?.item?.seoDescription
        : seoDescription;

    const condition = pdpDetailsDisplay.find(detail => detail?.detailKey === 'CONDITION');
    const conditionSummary = condition?.detailValues?.find(
        value => value?.detailKey === 'condition'
    );
    const conditionNotes = condition?.detailValues?.find(
        value => value?.detailKey === 'conditionDetails'
    );
    const conditionState = item?.condition?.state;

    const dimensions = pdpDetailsDisplay.find(detail => detail?.detailKey === 'DIMENSIONS');
    const featureDimensions = !!dimensions && (vertical === 'furniture' || vertical === 'art');

    const iconicDesign = pdpDetailsDisplay.find(detail => detail?.detailKey === 'ICONIC_DESIGN');
    const iconicDesignLink = iconicDesign?.detailValues?.find(
        value => value?.detailKey === 'iconicDesign'
    )?.browseUrl;

    const referenceNumber = pdpDetailsDisplay.find(
        detail => detail?.detailKey === 'REFERENCE_NUMBER'
    );
    const showInvoice =
        (isAvailable || hasActiveHoldForBuyer) &&
        !isSold &&
        !hasPrivateListing &&
        !isListingUnlisted &&
        seller?.isAccessible;

    return (
        <PdpRespSidebarDetailsSection expandedOnRender title={About} ga={{ trigger: gaTrigger }}>
            {description && (
                <div className={dibsCss.mbSmall}>
                    <ExpandingAreaWithReadMore
                        showGradient
                        lineClamp={4}
                        readMore={ReadMore}
                        readLess={ReadLess}
                        onReadMoreClick={trackReadMoreClick}
                    >
                        <>
                            {!!dispatch && !!interfaceState && (
                                <TranslationToggle
                                    className={dibsCss.mbSmaller}
                                    currentState={interfaceState}
                                    onClick={() => {
                                        dispatch({
                                            type: 'TOGGLE_TRANSLATIONS',
                                        });
                                    }}
                                />
                            )}
                            <div
                                className={dibsCss.whitespacePreLine}
                                data-tn="pdp-item-description-content"
                                dangerouslySetInnerHTML={{ __html: (description || '').trim() }}
                            />
                        </>
                    </ExpandingAreaWithReadMore>
                </div>
            )}
            <ExpandingAreaWithReadMore
                readMore={ViewMore}
                readLess={ViewLess}
                showGradient={false}
                onReadMoreClick={trackReadMoreClick}
                collapsedHeight={featureDimensions ? 159 : 75}
            >
                {
                    <div
                        className={cn(
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.gapXsmall,
                            dibsCss.sassyFontBodySmall
                        )}
                    >
                        {condition && conditionSummary && (
                            <div
                                className={cn(
                                    dibsCss.pSmaller,
                                    dibsCss.bgAlabaster,
                                    dibsCss.roundedXxsmall
                                )}
                            >
                                <div className={dibsCss.sassyFontBodySmallHeavy}>
                                    <div>{condition.detailDisplayName}</div>
                                    <div>
                                        <span
                                            className={cn({
                                                [dibsCss.textSuccesssecondary]:
                                                    conditionState === 'NEW' ||
                                                    conditionState === 'GOOD' ||
                                                    conditionState === 'EXCELLENT',
                                            })}
                                        >
                                            {conditionSummary.displayName}
                                        </span>{' '}
                                        <Link
                                            onClick={() => {
                                                trackConditionClick();
                                                setIsConditionModalOpen(true);
                                            }}
                                        >
                                            <FormattedMessage
                                                id="PdpRespSidebarDetailsAbout.conditionDetails"
                                                defaultMessage="Details"
                                            />
                                        </Link>
                                    </div>
                                </div>
                                <ClientSuspense fallback={null}>
                                    <PdpSharedConditionModal
                                        itemId={itemId}
                                        isOpen={isConditionModalOpen}
                                        onClose={() => setIsConditionModalOpen(false)}
                                    />
                                </ClientSuspense>
                            </div>
                        )}
                        {featureDimensions && (
                            <div
                                className={cn(
                                    dibsCss.flex,
                                    dibsCss.pSmaller,
                                    dibsCss.gapSmall,
                                    dibsCss.bgAlabaster,
                                    dibsCss.alignBaseline,
                                    dibsCss.roundedXxsmall
                                )}
                            >
                                {['height', 'width', 'depth', 'diameter', 'length'].map(
                                    dimension => {
                                        const dimensionDisplay = dimensions?.detailValues?.find(
                                            value => value?.detailKey === dimension
                                        );

                                        if (!dimensionDisplay || !dimensionDisplay.displayName) {
                                            return null;
                                        }

                                        const [label = '', name = ''] =
                                            dimensionDisplay.displayName.split(':');

                                        return (
                                            <div key={dimensionDisplay.detailKey}>
                                                <div className={dibsCss.sassyFontBodySmallHeavy}>
                                                    {label.trim()}
                                                </div>
                                                <div>{name.trim()}</div>
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                        )}
                        {conditionNotes && (
                            <div
                                className={cn(
                                    dibsCss.pSmaller,
                                    dibsCss.bgAlabaster,
                                    dibsCss.roundedXxsmall
                                )}
                            >
                                <div className={dibsCss.sassyFontBodySmallHeavy}>
                                    <FormattedMessage
                                        id="PdpRespSidebarDetailsAbout.conditionNotes"
                                        defaultMessage="Condition Notes"
                                    />
                                </div>
                                <div>
                                    <span>{conditionNotes.displayName}</span>
                                </div>
                            </div>
                        )}
                        <div
                            className={cn(
                                dibsCss.flex,
                                dibsCss.flexCol,
                                dibsCss.pSmaller,
                                dibsCss.gapSmaller,
                                dibsCss.bgAlabaster,
                                dibsCss.roundedXxsmall
                            )}
                        >
                            <>
                                {!!uniqueCreators?.length && (
                                    <div>
                                        <div className={dibsCss.sassyFontBodySmallHeavy}>
                                            {Creator}
                                        </div>
                                        {uniqueCreators.map(creator => {
                                            const {
                                                browseUrl,
                                                displayName,
                                                displayDetails,
                                                detailKey: key,
                                            } = creator || {};
                                            let displayValue = null;
                                            if (displayName && browseUrl) {
                                                displayValue = (
                                                    <Link href={browseUrl}>{displayName}</Link>
                                                );
                                            } else if (displayName) {
                                                displayValue = <div>{displayName}</div>;
                                            }
                                            if (displayValue && displayDetails) {
                                                displayValue = (
                                                    <div>
                                                        {displayValue} <span>{displayDetails}</span>
                                                    </div>
                                                );
                                            }
                                            return <Fragment key={key}>{displayValue}</Fragment>;
                                        })}
                                    </div>
                                )}
                                {!!storyTellingModule?.tabs?.length &&
                                    storyTellingModule.tabs.map(tab => {
                                        const {
                                            tabTitle,
                                            contentHeader,
                                            contentBodyText,
                                            tabType,
                                        } = tab || {};
                                        const isIconicDesigTab = tabType === 'iconic design';
                                        let tabLink = null;
                                        if (isIconicDesigTab && iconicDesignLink) {
                                            tabLink = iconicDesignLink;
                                        } else if (creatorLinkMap && contentHeader) {
                                            tabLink = creatorLinkMap[contentHeader.toLowerCase()];
                                        }
                                        return tabTitle && contentHeader ? (
                                            <ExpandingAreaWithReadMore
                                                key={`${tabType}-${tabTitle}`}
                                                readMore={
                                                    <span className={dibsCss.sassyFontBodySmall}>
                                                        {ViewMore}
                                                    </span>
                                                }
                                                readLess={
                                                    <span className={dibsCss.sassyFontBodySmall}>
                                                        {ViewLess}
                                                    </span>
                                                }
                                                collapsedHeight={42}
                                                showGradient={false}
                                            >
                                                <>
                                                    <div
                                                        className={dibsCss.sassyFontBodySmallHeavy}
                                                    >
                                                        {tabTitle}
                                                    </div>
                                                    <div>
                                                        {tabLink ? (
                                                            <Link href={tabLink}>
                                                                {contentHeader}
                                                            </Link>
                                                        ) : (
                                                            contentHeader
                                                        )}
                                                    </div>
                                                    {contentBodyText && (
                                                        <div
                                                            className={cn(
                                                                dibsCss.mtSmaller,
                                                                dibsCss.whitespacePreLine
                                                            )}
                                                            dangerouslySetInnerHTML={{
                                                                __html: contentBodyText,
                                                            }}
                                                        />
                                                    )}
                                                    {tab && (
                                                        <div className={dibsCss.mtSmaller}>
                                                            <PdpSharedStoryTellingShopAllLink
                                                                storyTellingTab={tab}
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            </ExpandingAreaWithReadMore>
                                        ) : null;
                                    })}
                                {pdpDetailsDisplay
                                    .filter(
                                        detail =>
                                            detail?.detailKey &&
                                            !CUSTOM_DETAILS.includes(detail.detailKey)
                                    )
                                    .map(detail => {
                                        const {
                                            detailKey,
                                            delimiter,
                                            detailValues,
                                            detailDisplayName,
                                        } = detail || {};

                                        return detailDisplayName && detailValues?.length ? (
                                            <div key={detailKey}>
                                                <div
                                                    className={cn(dibsCss.sassyFontBodySmallHeavy)}
                                                >
                                                    {detailDisplayName}
                                                </div>
                                                <div
                                                    className={cn(
                                                        dibsCss.flex,
                                                        dibsCss.gap3px,
                                                        dibsCss.flexWrap
                                                    )}
                                                >
                                                    {detailValues.map((value, index) => {
                                                        if (
                                                            skuId &&
                                                            value?.skuId &&
                                                            value.skuId !== skuId
                                                        ) {
                                                            return null;
                                                        }
                                                        if (detail?.detailKey === 'RING_SIZE') {
                                                            return (
                                                                <PdpSharedDetailRingSize
                                                                    currency={currency}
                                                                    detailValues={detailValues.filter(
                                                                        filterNulls
                                                                    )}
                                                                />
                                                            );
                                                        }
                                                        const {
                                                            browseUrl,
                                                            displayName,
                                                            displayDetails,
                                                            detailKey: key,
                                                        } = value || {};
                                                        let displayValue = null;
                                                        if (browseUrl && displayName) {
                                                            displayValue = (
                                                                <span>
                                                                    <Link href={browseUrl}>
                                                                        {displayName}
                                                                    </Link>
                                                                </span>
                                                            );
                                                        } else if (displayName) {
                                                            displayValue = (
                                                                <span>{displayName}</span>
                                                            );
                                                        }
                                                        if (displayValue && displayDetails) {
                                                            displayValue = (
                                                                <span>
                                                                    {displayValue}{' '}
                                                                    {` ${displayDetails}`}
                                                                </span>
                                                            );
                                                        }
                                                        if (
                                                            displayValue &&
                                                            delimiter &&
                                                            index < detailValues.length - 1
                                                        ) {
                                                            displayValue = (
                                                                <span>
                                                                    {displayValue}
                                                                    {delimiter}
                                                                </span>
                                                            );
                                                        }
                                                        return (
                                                            <Fragment
                                                                key={`${key}-${index}-${displayName}`}
                                                            >
                                                                {displayValue}
                                                            </Fragment>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : null;
                                    })}
                                {referenceNumber?.detailValues?.length && (
                                    <div>
                                        {referenceNumber?.detailDisplayName && (
                                            <div className={cn(dibsCss.sassyFontBodySmallHeavy)}>
                                                {referenceNumber?.detailDisplayName}
                                            </div>
                                        )}
                                        {referenceNumber.detailValues.map((value, index) => {
                                            if (!value?.displayName) {
                                                return null;
                                            }
                                            if (skuId && value?.skuId && value.skuId !== skuId) {
                                                return null;
                                            }
                                            return (
                                                <div key={`${index}-${value.displayName}`}>
                                                    {value.displayName}
                                                </div>
                                            );
                                        })}
                                        {showInvoice && (
                                            <PdpSharedDetailItemInvoice
                                                item={item}
                                                user={user}
                                                skuId={skuId}
                                            />
                                        )}
                                    </div>
                                )}
                            </>
                        </div>
                    </div>
                }
            </ExpandingAreaWithReadMore>
        </PdpRespSidebarDetailsSection>
    );
};
